import React from 'react';
import { Heading } from '@klp/design/Heading';
import { Grid } from '@material-ui/core';
import type { ContractToRegistration } from '../classes';
import { formatDate, parseSafeDate } from '@klp/utils/dateTimeFormatter';
import { useTranslation } from 'react-i18next';
import type { ISignatory } from '../classes/ISignatory';
import { Block, Link, Typography } from '@klp/design/index';
import { ContractDocument } from './ContractDocument';

const renderReceivers = (receivers?: Array<ISignatory>) => {
    if (!receivers || receivers.length === 0) {
        return <Typography translation="checkOffContract.noReceivers" />;
    }

    return receivers.map((receiver) => (
        <Grid key={receiver.epost}>
            <Link klpTypographyVariant="small" klpColor="main">
                <Typography klpTypographyVariant="small">
                    {receiver.epost}
                </Typography>
            </Link>
            <Link href={`mailto: ${receiver.epost}`} />
        </Grid>
    ));
};

type ContractSummaryProps = { contract: ContractToRegistration };

export const ContractSummary: React.FC<ContractSummaryProps> = ({
    contract,
}) => {
    const { t } = useTranslation();
    return (
        <Block paddingTop="compact" paddingBottom="tiny">
            <Heading klpHeadingLevel="ONE">{contract.navn}</Heading>
            {contract.kundeNavn && contract.kundeNummer && (
                <Heading
                    klpHeadingLevel="FOUR"
                    klpColor="dark"
                    translation="checkOffContract.subTitle"
                    translationOptions={{
                        kundeNavn: contract.kundeNavn,
                        kundeNummer: contract.kundeNummer,
                    }}
                    data-cy="LevendeKontrakter__Title"
                />
            )}
            <Grid container={true} direction="row">
                <Block marginRight="fat">
                    <Typography>
                        {t('checkOffContract.signed')}
                        {formatDate(parseSafeDate(contract.signertDato))}
                    </Typography>
                </Block>
                <Block>
                    <Typography>
                        {t('checkOffContract.sender')}
                        {contract.ansvarligNavn ? (
                            <>{contract.ansvarligNavn}</>
                        ) : (
                            t('checkOffContract.noSender')
                        )}
                    </Typography>
                </Block>
            </Grid>
            <Block marginTop="compact">
                {contract.filMetadata ? (
                    <ContractDocument contractDocument={contract.filMetadata} />
                ) : (
                    <Typography translation="checkOffContract.noDocuments" />
                )}
            </Block>

            <Block paddingTop="compact">
                <Heading
                    klpHeadingLevel="TWO"
                    klpColor="dark"
                    translation="checkOffContract.receivers"
                    translationOptions={{
                        kontraktnavn: contract?.kontraktsNummer,
                    }}
                />
                {renderReceivers(contract.underskrivere)}
            </Block>
        </Block>
    );
};
